/** Application shell: sidebar navigation, top bar with language + user menu. */
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import {
  Bell,
  BookOpen,
  Boxes,
  CalendarDays,
  FileSpreadsheet,
  Files,
  Settings,
  CalendarClock,
  ClipboardList,
  Receipt,
  LayoutDashboard,
  LogOut,
  Map as MapIcon,
  Menu,
  Ship,
  Siren,
  Warehouse,
  X,
} from "lucide-react";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "@/api/client";
import { unwrap } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { firstAdminRoute } from "@/features/admin/AdminLayout";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { cn } from "@/shared/lib/format";
import { SmartWatersMark, ThemeToggle } from "@/shared/ui/brand";
import { Button } from "@/shared/ui/button";
import { Select } from "@/shared/ui/form";

/** A sidebar entry. */
interface NavItem {
  to: string;
  labelKey: string;
  icon: typeof Ship;
  /** Any of these permissions shows the entry. */
  permission?: string[];
  /** Module required to show the entry. */
  module?: string;
}

/** Sidebar entries in display order. */
export const NAV_ITEMS: NavItem[] = [
  { to: "/", labelKey: "nav.dashboard", icon: LayoutDashboard },
  { to: "/map", labelKey: "nav.fleetMap", icon: MapIcon, permission: ["assets.position.view"] },
  { to: "/assets", labelKey: "nav.assets", icon: Ship, permission: ["assets.asset.view"] },
  {
    to: "/work-orders",
    labelKey: "nav.workOrders",
    icon: ClipboardList,
    permission: ["workorders.workorder.view_assigned", "workorders.workorder.view_all"],
  },
  {
    to: "/requests",
    labelKey: "nav.requests",
    icon: Siren,
    permission: ["requests.request.view_own", "requests.request.view_all"],
  },
  {
    to: "/calendar",
    labelKey: "nav.calendar",
    icon: CalendarDays,
    permission: ["maintenance.plan.view", "assets.certificate.view"],
  },
  {
    to: "/maintenance",
    labelKey: "nav.maintenance",
    icon: CalendarClock,
    permission: ["maintenance.plan.view"],
  },
  { to: "/inventory", labelKey: "nav.inventory", icon: Boxes, permission: ["inventory.item.view"] },
  {
    to: "/warehouse",
    labelKey: "warehouse.title",
    icon: Warehouse,
    permission: ["inventory.stock.view"],
    module: "inventory",
  },
  {
    to: "/documents",
    labelKey: "documentLibrary.title",
    icon: Files,
    permission: ["documents.document.view"],
    module: "documents",
  },
  {
    to: "/billing",
    labelKey: "finance.title",
    icon: Receipt,
    permission: ["reporting.report.cost", "billing.chargeback.view"],
  },
  {
    to: "/reports",
    labelKey: "nav.reports",
    icon: FileSpreadsheet,
    permission: ["reporting.report.export"],
  },
  { to: "/notifications", labelKey: "nav.notifications", icon: Bell },
  { to: "/docs", labelKey: "docs.title", icon: BookOpen },
];

/** Poll the unread notification count for the bell badge. */
export function useUnreadCount(): number {
  const { status } = useAuth();
  const query = useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: async () => unwrap(await api.GET("/api/v1/notifications/unread-count")),
    enabled: status === "authenticated",
    refetchInterval: 30_000,
  });
  return (query.data as { unread?: number } | undefined)?.unread ?? 0;
}

/** Language selector bound to i18next. */
export function LanguageSwitcher(): ReactElement {
  const { i18n, t } = useTranslation();
  return (
    <Select
      aria-label={t("app.language")}
      className="min-w-32 w-auto pe-8"
      value={i18n.resolvedLanguage ?? "en"}
      onChange={(e) => void i18n.changeLanguage(e.target.value)}
    >
      {SUPPORTED_LANGUAGES.map((l) => (
        <option key={l.code} value={l.code}>
          {l.label}
        </option>
      ))}
    </Select>
  );
}

/** Shell layout rendered for every authenticated route. */
export function AppShell(): ReactElement {
  const { t } = useTranslation();
  const { me, can, hasModule, switchTenant, logout } = useAuth();
  const unread = useUnreadCount();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [navigationOpen, setNavigationOpen] = useState(false);

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const closeOnDesktop = (): void => {
      if (desktop.matches) {
        setNavigationOpen(false);
      }
    };
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, []);

  const items = NAV_ITEMS.filter(
    (item) =>
      (!item.permission || item.permission.some(can)) && (!item.module || hasModule(item.module)),
  );
  const adminTo = firstAdminRoute(can);

  const navigation = (
    <>
      <div className="flex h-11 items-center gap-2 border-b bg-card px-3">
        <SmartWatersMark className="size-6" />
        <div className="min-w-0 leading-tight">
          <div className="flex items-baseline gap-1.5">
            <span className="text-sm font-bold tracking-tight text-foreground">
              {t("app.brand")}
            </span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-brand-foreground">
              Smart Waters
            </span>
          </div>
          <div className="truncate text-[11px] text-muted-foreground">{me?.active_tenant.name}</div>
        </div>
      </div>
      <nav className="flex-1 space-y-0.5 p-1.5" aria-label={t("app.mainNavigation")}>
        {items.map((item) => {
          const active = item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setNavigationOpen(false)}
              className={cn(
                "flex min-h-8 items-center gap-2 rounded-sm border border-transparent px-2.5 py-1.5 text-xs text-foreground/85 hover:bg-accent hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring [@media(pointer:coarse)]:min-h-11",
                active &&
                  "border-brand/40 border-s-2 border-s-brand bg-secondary font-semibold text-foreground",
              )}
              aria-current={active ? "page" : undefined}
            >
              <Icon
                className={cn(
                  "h-3.5 w-3.5 shrink-0 text-muted-foreground",
                  active && "text-brand-foreground",
                )}
              />
              <span className="flex-1">{t(item.labelKey)}</span>
              {item.to === "/notifications" && unread > 0 ? (
                <span className="rounded-sm bg-brand px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-primary">
                  {unread}
                </span>
              ) : null}
            </Link>
          );
        })}
        {adminTo ? (
          <Link
            to={adminTo}
            onClick={() => setNavigationOpen(false)}
            className={cn(
              "mt-1.5 flex min-h-8 items-center gap-2 rounded-sm border-t px-2.5 py-1.5 pt-2.5 text-xs text-foreground/85 hover:bg-accent hover:text-foreground [@media(pointer:coarse)]:min-h-11",
              pathname.startsWith("/admin") &&
                "border-s-2 border-s-brand bg-secondary font-semibold text-foreground",
            )}
            aria-current={pathname.startsWith("/admin") ? "page" : undefined}
          >
            <Settings
              className={cn(
                "h-3.5 w-3.5 shrink-0 text-muted-foreground",
                pathname.startsWith("/admin") && "text-brand-foreground",
              )}
            />
            <span className="flex-1">{t("nav.admin")}</span>
          </Link>
        ) : null}
      </nav>
    </>
  );

  return (
    <div className="flex min-h-dvh bg-background text-foreground">
      <a
        href="#main-content"
        className="sr-only z-50 rounded-md bg-background p-3 focus:not-sr-only focus:fixed focus:start-3 focus:top-3"
      >
        {t("app.skipToContent")}
      </a>
      <aside className="hidden w-52 shrink-0 flex-col border-e bg-sidebar text-sidebar-foreground lg:flex">
        {navigation}
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex min-h-11 flex-wrap items-center justify-between gap-2 border-b bg-card px-3 py-1 sm:px-4">
          <DialogPrimitive.Root open={navigationOpen} onOpenChange={setNavigationOpen}>
            <DialogPrimitive.Trigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                aria-label={t("app.openNavigation")}
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </Button>
            </DialogPrimitive.Trigger>
            <DialogPrimitive.Portal>
              <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50" />
              <DialogPrimitive.Content
                aria-describedby={undefined}
                className="fixed inset-y-0 start-0 z-50 flex w-72 max-w-[calc(100%-2rem)] flex-col overflow-y-auto overscroll-contain border-e bg-sidebar pb-[env(safe-area-inset-bottom)] shadow-lg"
              >
                <div className="flex items-center justify-between border-b bg-card px-3 py-1.5">
                  <DialogPrimitive.Title className="text-sm font-semibold">
                    {t("app.mainNavigation")}
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Close asChild>
                    <Button variant="ghost" size="icon" aria-label={t("app.close")}>
                      <X className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </DialogPrimitive.Close>
                </div>
                {navigation}
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          </DialogPrimitive.Root>
          <div className="ms-auto flex min-w-0 items-center gap-2">
            {me && me.tenants.length > 1 ? (
              <Select
                aria-label={t("auth.workspace")}
                className="min-w-28 w-auto pe-7 text-xs"
                value={me.active_tenant.slug}
                onChange={(e) => void switchTenant(e.target.value)}
              >
                {me.tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.slug}>
                    {tenant.name}
                  </option>
                ))}
              </Select>
            ) : null}
            <ThemeToggle />
            <Link
              to="/docs"
              className="flex size-8 shrink-0 items-center justify-center rounded-sm hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring [@media(pointer:coarse)]:size-11"
              aria-label={t("docs.title")}
            >
              <BookOpen className="h-4 w-4" aria-hidden="true" />
            </Link>
            <LanguageSwitcher />
            <Link
              to="/account"
              aria-label={t("account.title")}
              className="max-w-48 truncate rounded-sm px-2 py-1 text-xs font-medium hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring"
            >
              {me?.display_name ?? me?.email ?? t("account.title")}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t("app.signOut")}
              onClick={() => void logout()}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>
        <main
          id="main-content"
          tabIndex={-1}
          className="min-w-0 flex-1 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] outline-none sm:p-4"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
